import { CloseOutlined } from '@ant-design/icons';
import { Alert, Button, Checkbox, Heading } from '@mm/components';
import { selectEmailAccounts } from '@mm/state/selectors/emailAccountsSelectors';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styles from './ContactDeleteModal.module.css';

interface Props {
  showEraseRemoteMessagesOption: boolean;
  show: boolean;
  emailAddress: string;
  onClose: () => void;
  onDelete: (eraseRemoteMessages: boolean) => void;
}

export const ContactDeleteModal = ({
  showEraseRemoteMessagesOption,
  show,
  emailAddress,
  onClose,
  onDelete,
}: Props) => {
  const emailAccounts = useSelector(selectEmailAccounts).filter(
    account => account.canEraseRemoteMessages,
  );
  const allowEraseRemoteMessages = emailAccounts.length > 0;
  const [eraseRemoteMessages, setEraseRemoteMessages] = useState(false);
  const [reallyEraseRemoteMessages, setReallyEraseRemoteMessages] =
    useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);

  useEffect(() => {
    if (!show) {
      setEraseRemoteMessages(false);
      setReallyEraseRemoteMessages(false);
    }
  }, [show]);

  if (!show) {
    return null;
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.content}>
        <CloseOutlined className={styles.closeButton} onClick={onClose} />
        <Heading size={3}>Permanently delete contact?</Heading>
        <div>
          Clicking DELETE will permanently destroy all records of this contact,
          including past conversations and attributes, that are stored on
          Customers.ai’s servers. There is no way to undo this action.
        </div>
        {allowEraseRemoteMessages && showEraseRemoteMessagesOption && (
          <div className={styles.gdprOptionArea}>
            <Heading size={4}>GDPR Compliance</Heading>
            <Checkbox
              isChecked={eraseRemoteMessages}
              onToggle={e => setEraseRemoteMessages(e.target.checked)}
              label="Permanently delete all email messages involving this contact from all connected email accounts"
              labelHelpTextClassName={styles.gdprOptionLabel}
            />
            {eraseRemoteMessages && (
              <Alert
                type="warning"
                showCloseIcon={false}
                css={{ container: styles.gdprAlert }}
              >
                This will <strong>permanently delete all email messages</strong>{' '}
                to or from the following email address:{' '}
                <strong>{emailAddress}</strong>.
                <br />
                <br />
                Email messages will be <strong>permanently deleted</strong> from
                in the following mailboxes:
                <ul>
                  {emailAccounts.map(account => (
                    <li>{account.emailAddress}</li>
                  ))}
                </ul>
              </Alert>
            )}
            {eraseRemoteMessages && (
              <Checkbox
                isChecked={reallyEraseRemoteMessages}
                onToggle={e => setReallyEraseRemoteMessages(e.target.checked)}
                label="I accept that this option will permanently remove messages from my email accounts, and there is no way to undo this action"
                labelHelpTextClassName={styles.gdprConfirmOptionLabel}
              />
            )}
          </div>
        )}
        <div className={styles.buttonArea}>
          <Button color="transparent-no-shadow" size="lg" onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="red"
            size="lg"
            disabled={
              deleteInProgress ||
              (eraseRemoteMessages && !reallyEraseRemoteMessages)
            }
            onClick={async () => {
              setDeleteInProgress(true);
              try {
                await onDelete(reallyEraseRemoteMessages);
                onClose();
              } finally {
                setDeleteInProgress(false);
              }
            }}
          >
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
};
